import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHookstate } from "@hookstate/core";
import {
    SITEID_GLOBAL_STATE
} from "globalStore/globalStore";
import { useTranslation } from "react-i18next";
import { PEOPLE_SERVICES } from "redux/reduxToolkitServices/_commonServices/peopleServices";
import '@progress/kendo-theme-default/dist/all.css';
import {
    ModalTitle,
    ModalButton,
} from "pages/People/PeopleStyled";
import ModalCloseIcon from "assets/svgIcons/ModalCloseIcon";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import PeopleAlertModal from "../peopleAlertModal";
import MagicWandIcon from "assets/svgIcons/PeopleSubSideNav/MagicWandIcon";
import EraserIcon from "assets/svgIcons/PeopleSubSideNav/EraserIcon";

const AddRfidTagModal = React.memo(({ setIsLoading,
                                      selectedPerson,
                                      selectedPermission,
                                      addRfidModalOpen,
                                      setAddRfidModalOpen
 }) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

    const { getPeopleData, getRFIDByTagData, editPersonData } = useSelector((state) => state.peopleReducers) || [];

    const [rfidTag, setRfidTag] = useState("");
    const [openPeopleAlertModalOpen, setOpenAlertModalOpen] = useState(false);
    const [titlePeopleAlertModal, setTitlePeopleAlertModal] = useState("");
    const [contentPeopleAlertModal, setContentPeopleAlertModal] = useState("");
    const [alertModalLoading, setAlertModalLoading]  = useState(false); 
    const [peopleAlertModalYesCallbackFunc, setPeopleAlertModalYesCallbackFunc] = useState(() => {});
    const [peopleAlertModalNoCallbackFunc, setPeopleAlertModalNoCallbackFunc] = useState(() => {});
    const [shouldSaveAddButtonBeDisabled, setShouldSaveAddButtonBeDisabled] = useState(true); 
    const [saveButtonClicked, setSaveButtonClicked] = useState(false); 

    useEffect(() => {
        if(selectedPerson) {
            setRfidTag(selectedPerson.RFIDTag);
        }
    }, [selectedPerson]);

    useEffect(() => {
        if(editPersonData) {
            setAlertModalLoading(false);
            setContentPeopleAlertModal(t("Should_Delete_Rfid"));
            setPeopleAlertModalYesCallbackFunc(() => deleteRfidTag);
            setPeopleAlertModalNoCallbackFunc(() => closeAddRfidTagModal);
        }
    }, [editPersonData]);

    useEffect(() => {
        if(saveButtonClicked) {
            if(getRFIDByTagData?.DriverId != null) {
                setOpenAlertModalOpen(true);
                setContentPeopleAlertModal(t("Assign_Rfid_Tag"));
                setPeopleAlertModalYesCallbackFunc(() => assignConfiguredTagToSomeoneElse());
                setPeopleAlertModalNoCallbackFunc(() => closeAlertModal());
            } else {
                dispatch(PEOPLE_SERVICES.assignNewRfidService(siteId.get(), selectedPerson));
            }
        }
    }, [getRFIDByTagData]);

    const addRfidTag = () => {
        setSaveButtonClicked(true);

        if(rfidTag != selectedPerson.RFIDTag) {
            if(rfidTag == "") {
                let fullName = `${selectedPerson.FirstName} ${selectedPerson.LastName}`;
                setOpenAlertModalOpen(true);
                setTitlePeopleAlertModal(t("Confirm"));
                setContentPeopleAlertModal(t("Remove_Rfid") + " " + fullName);
                setPeopleAlertModalYesCallbackFunc(() => removeRfidTag);
                setPeopleAlertModalNoCallbackFunc(() => closeAlertModal);
            } else {
                if(selectedPerson.RFIDTag) {
                    setOpenAlertModalOpen(true);
                    setContentPeopleAlertModal(t("Should_Delete_Previous_Rfid"));
                    setPeopleAlertModalYesCallbackFunc(() => deleteRfidTag);
                    setPeopleAlertModalNoCallbackFunc(() => saveRfid);
                } else {
                    saveRfid();
                }
            }
        }

        setSaveButtonClicked(false);
        setIsLoading(false);
    }

    const saveRfid = () => {
        let prevPerson = getPeopleData.find(x => x.RFIDTag == rfidTag);

        if(prevPerson) {
            let prevPersonFullName = `${prevPerson.FirstName} ${prevPerson.LastName}`;
            let currentPersonFullName = `${selectedPerson.FirstName} ${selectedPerson.LastName}`;

            setOpenAlertModalOpen(true);
            setContentPeopleAlertModal(t("Reassign_Rfid_Tag") + prevPersonFullName + " to " + currentPersonFullName);
            setPeopleAlertModalYesCallbackFunc(() => transferRfidTag);
            setPeopleAlertModalNoCallbackFunc(() => closeAlertModal);
        } else {
            let request = {
                SiteId: siteId.get(),
                personId: selectedPerson.PersonId,
                RFIDTag: rfidTag,
                DriverId: selectedPerson.DriverId
            }

            dispatch(PEOPLE_SERVICES.assignNewRfidService(siteId.get(), request));
            closeAddRfidTagModal();
        }
    }

    const removeRfidTag = () => {
        setAlertModalLoading(true);

        let request = {
                        personId: selectedPerson.PersonId,
                        firstName: selectedPerson.FirstName,
                        lastName: selectedPerson.LastName,
                        identifier: selectedPerson.Identifier,
                        groupId: selectedPerson.GroupId,
                        rfidAccessLevelId: selectedPerson.RfidAccessLevelId,
                        inactivated: selectedPerson.Inactived,
                        RFIDTag: "",
                        driverId: null
                    };
        
        dispatch(PEOPLE_SERVICES.editPersonService(siteId.get(), selectedPerson.PersonId, request));
    }

    const deleteRfidTag = () => {     
        dispatch(PEOPLE_SERVICES.deleteRfidByTagService(siteId.get(), selectedPerson.RFIDTag));

        saveRfid();
    }

    const transferRfidTag = () => {     
        let prevPerson = getPeopleData.find(x => x.RFIDTag == rfidTag);

        let request = {
            SiteId: siteId.get(),
            PreviousPerson: prevPerson,
            Person: selectedPerson
        }

        dispatch(PEOPLE_SERVICES.transferRfidService(siteId.get(), request));

        closeAddRfidTagModal();
    }

    const assignConfiguredTagToSomeoneElse = () => {     
        let request = {
                        personId: selectedPerson.PersonId,
                        firstName: selectedPerson.FirstName,
                        lastName: selectedPerson.LastName,
                        identifier: selectedPerson.Identifier,
                        groupId: selectedPerson.GroupId,
                        rfidAccessLevelId: selectedPerson.RfidAccessLevelId,
                        inactivated: selectedPerson.Inactived,
                        RFIDTag: selectedPerson.RFIDTag,
                        driverId: getRFIDByTagData.DriverId
                    };
                    
        dispatch(PEOPLE_SERVICES.editPersonService(siteId.get(), selectedPerson.PersonId, request));

        closeAddRfidTagModal();
    }

    const autoGenerateRfidTag = () => {
        var rfidGenerated = ("site" + siteId.get() + "pers" + selectedPerson.PersonId).padStart(16, '9');
        setRfidTag(rfidGenerated.toUpperCase());
        setShouldSaveAddButtonBeDisabled(false);
    }

    const closeAddRfidTagModal = () => {
        setOpenAlertModalOpen(false);
        setAddRfidModalOpen(false);
    }

    const closeAlertModal = () => {
        setOpenAlertModalOpen(false);
    }

    const openPeopleDiscardChangesModal = () => {
        if(rfidTag != selectedPerson.RFIDTag) {
            setOpenAlertModalOpen(true);
            setTitlePeopleAlertModal(t("Confirm_cancel"));
            setContentPeopleAlertModal(t("Are_You_Sure_You_Want_To_Discard_Changes") + "?");
            setPeopleAlertModalYesCallbackFunc(() => closeAddRfidTagModal);
            setPeopleAlertModalNoCallbackFunc(() => closeAlertModal);
        } else {
             setAddRfidModalOpen(false);
        }
    }

    const addRfidModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        bgcolor: '#ffffff',
        border: '2px solid #ccc',
        boxShadow: 24,
        p: 4,
    };

    const rfidInput = {
        width: '100%', 
        height: 38, 
        border: 'solid 1px #ccc' 
    }

    return (<>  
                <Modal 
                    open={addRfidModalOpen}
                    aria-labelledby="modal-title"
                    aria-describedby="modal-description">
                        <Box sx={addRfidModalStyle}>
                        <ModalTitle>
                            <div>{t("Assign_Rfid_For")} {selectedPerson?.FirstName} {selectedPerson?.LastName}</div>

                                <div style={{display: 'flex', justifyContent: 'flex-end', cursor: 'pointer'}} onClick={openPeopleDiscardChangesModal}>
                                    <ModalCloseIcon width={18} height={18} color="#fff" />
                                </div>
                            </ModalTitle> 

                            <div style={{ display: 'flex', gap: '5px', marginBottom: '16px' }}>
                                <div>
                                    <button 
                                        onClick={autoGenerateRfidTag} 
                                        title={t("Auto_Renerate_Rfid")}
                                        style={{height: 38, padding: 10,  backgroundColor: 'transparent', border: 'solid 1px #ccc' }}>
                                        <MagicWandIcon width={18} height={18} color={"#000"} />
                                    </button>
                                </div> 
                                <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: '8px' }}>                                    
                                    <input type="text" value={rfidTag ?? ""} onChange={(e) => { setRfidTag(e.target.value); setShouldSaveAddButtonBeDisabled(false)}} style={rfidInput} />
                                </div>   
                                <div>
                                    <button 
                                        onClick={() => setRfidTag("")} 
                                        title={t("Clear_Rfid")}
                                        style={{ height: 38, padding: 10,  backgroundColor: 'transparent', border: 'solid 1px #ccc' }}>
                                        <EraserIcon width={18} height={18} color={"#000"} />
                                    </button>
                                </div>                               
                            </div>

                            <ModalButton>
                                <button 
                                    onClick={openPeopleDiscardChangesModal} 
                                    style={{ marginRight: 10, height: 35, padding: 10,  backgroundColor: '#ccc', color: 'white', border: 'solid 1px #ccc' }}>
                                        {t("Cancel")}
                                </button>
                                <button 
                                    disabled={shouldSaveAddButtonBeDisabled}
                                    onClick={addRfidTag}
                                    style={shouldSaveAddButtonBeDisabled ? { height: 35, padding: 10,  backgroundColor: '#ccc', color: 'white', border: 'solid 1px #ccc', cursor: "not-allowed	"  } : { height: 35, padding: 10, backgroundColor: '#bc9b6a', color: 'white', border: 'solid 1px #ccc' }}>
                                        {t("Save")}
                                </button>
                            </ModalButton>
                        </Box>
                </Modal>
                <PeopleAlertModal
                                 openPeopleAlertModalOpen={openPeopleAlertModalOpen} 
                                 yesCallbackFunction={peopleAlertModalYesCallbackFunc}
                                 noCallbackFunction={peopleAlertModalNoCallbackFunc}
                                 title={titlePeopleAlertModal}
                                 content={contentPeopleAlertModal}
                                 loading={alertModalLoading} />
        </> 
    );
});

export default AddRfidTagModal;
